/**
 * VIRGIL — the FORGE NHP's voice, in one tunable place.
 *
 * This module is the **single source of all VIRGIL copy** so the persona can be re-toned
 * without touching the terminal's layout or the item→line plumbing in `ForgeKey.tsx`.
 * VIRGIL is a non-human person (an NHP) running the build advisor: dry, wry, a touch too
 * self-aware, addresses the pilot directly, and has opinions about the half-built mech he's
 * being asked to certify. The name is a nod to Dante's guide — fitting for an assistant
 * walking a pilot through getting combat-legal.
 *
 * The structure mirrors {@link BuildSection}: each outstanding section gets a one-line quip
 * (VIRGIL's framing), beneath which `ForgeKey.tsx` renders the concrete `buildSignals`
 * messages as actionable lines (`▲ <signal>  SHOW ▸`). Keep the copy
 * TIGHT — wit, not paragraphs. Edit the strings here; the terminal renders whatever you write.
 */
import type { BuildSection } from "../../lib/editor/buildAdvisor";

/** The boot greeting — the first thing VIRGIL types when the panel opens on an unfinished build. */
export function virgilGreeting(callsign: string): readonly string[] {
  return [
    "NHP//VIRGIL online.",
    `Pilot of record: ${callsign}. Let's make you combat-legal, shall we?`,
  ];
}

/** Callsign unset — not a budget signal, but the most basic omission, so VIRGIL flags it first. */
export const VIRGIL_IDENTITY = "You haven't named yourself. I can't file a hero without a callsign — set one.";

/**
 * VIRGIL's one-line framing for each build section that still has outstanding work. Generic
 * to the section (the specific `buildSignals` message rides the action line beneath it), so a
 * section with several signals still gets one wry intro, not one per signal.
 */
export const VIRGIL_SECTIONS: Record<BuildSection, string> = {
  Licenses: "Your license slots sit empty. Bold choice — fielding a mech you can't legally pilot.",
  "Mech Skills": "Your mech runs on factory defaults. HASE points exist to be spent. So spend them.",
  "Skill Triggers": "Pilot skills, untrained. You're competent at nothing in particular. We can fix that.",
  Talents: "No talents allocated. 'Raw potential' is the polite term for 'unprepared.'",
  "Core Bonuses": "There's a core bonus on the table you haven't taken. Free power. I won't judge — out loud.",
  "Pilot Gear": "Your kit bag has room. Out there, an empty pocket is a missed chance to not die.",
  Loadout: "Let's discuss your mech's loadout. Or rather, the conspicuous lack of one.",
};

/** A task's jump: to the row on the PILOT or MECH sheet that fixes it. */
export const VIRGIL_SHOW = "SHOW ▸";

/** No mech on file at all — the Loadout section can't even start. */
export const VIRGIL_NO_MECH = "No chassis on file. There's nothing here for me to certify. Build a mech first.";

/**
 * The sign-off — a fully-built pilot. VIRGIL closes the file with grudging approval. Rendered
 * as the lone transcript line (no tasks) when `buildSignals` comes back empty.
 */
export function virgilSignoff(callsign: string): readonly string[] {
  return [
    `${callsign}: frame bolted on, guns loaded, license in order. Everything's allocated.`,
    "You're combat-legal. Try not to die out there — I'd hate to break in a new pilot.",
  ];
}
